import { useState } from "react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

export default function Hero() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're on the list. Investment updates coming soon.");
      setEmail("");
    } catch (err) {
      toast.error("Couldn't subscribe right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="top" className="relative pt-28 lg:pt-36 pb-24 lg:pb-32 hero-rad overflow-hidden grain">
      <div className="absolute right-[-12%] top-[-12%] w-[520px] h-[520px] rounded-full bg-[hsl(var(--amber))]/30 blur-3xl glow-pulse pointer-events-none" />
      <div className="absolute left-[-10%] bottom-[-20%] w-[520px] h-[520px] rounded-full bg-[hsl(var(--terracotta))]/20 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 rise">
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">
              Renewable energy · Education · Fintech
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[hsl(var(--espresso))]"
            >
              Powering Africa's <br />
              <span className="italic">future</span> with{" "}
              <span className="shimmer-text">solar capital</span>.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-[hsl(var(--fg-muted))]">
              ASELU Investment Growth Limited mobilises capital for solar farming across Africa,
              an online education platform for new careers, and{" "}
              <a
                href="https://parachpay.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[hsl(var(--terracotta))] hover:text-[hsl(var(--terracotta-deep))] underline decoration-[hsl(var(--amber))] decoration-2 underline-offset-4 transition-colors"
                data-testid="hero-parachpay-link"
              >
                ParachPay
              </a>{" "}
              — fintech for sending, receiving and moving money between people and merchants.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a href="#portfolio" className="btn-pill btn-primary" data-testid="hero-cta-primary">
                Explore investments
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </a>
              <a href="#contact" className="btn-pill btn-ghost" data-testid="hero-cta-secondary">
                Contact our team
              </a>
            </div>

            <form
              onSubmit={subscribe}
              data-testid="hero-newsletter-form"
              className="mt-7 max-w-md"
            >
              <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--fg-muted))] font-semibold mb-2.5 inline-flex items-center gap-2">
                <i className="fa-solid fa-envelope-open-text text-[hsl(var(--terracotta))]"></i>
                Get exclusive investment updates
              </div>
              <div className="flex items-center gap-2 p-1.5 rounded-full bg-white border border-[hsl(var(--line))] shadow-sm focus-within:border-[hsl(var(--terracotta))] focus-within:ring-2 focus-within:ring-[hsl(var(--terracotta))]/20 transition">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@investor.com"
                  className="flex-1 bg-transparent px-4 py-2.5 text-sm outline-none placeholder:text-[hsl(var(--fg-muted))]"
                  data-testid="hero-newsletter-input"
                  required
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="btn-pill btn-primary !py-2.5 !px-5 text-sm disabled:opacity-60"
                  data-testid="hero-newsletter-submit"
                >
                  {busy ? (
                    <i className="fa-solid fa-circle-notch fa-spin text-xs"></i>
                  ) : (
                    <>
                      Subscribe
                      <i className="fa-solid fa-arrow-right text-[10px]"></i>
                    </>
                  )}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-[hsl(var(--fg-muted))]">
                Quarterly reports, deal flow and market insights. No spam, unsubscribe any time.
              </p>
            </form>


            <dl className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--fg-muted))]">Track record</dt>
                <dd className="mt-2 font-display text-3xl font-bold text-[hsl(var(--terracotta-deep))]" data-testid="stat-years">
                  6<span className="text-[hsl(var(--amber))]">+</span>
                </dd>
                <dd className="text-xs text-[hsl(var(--fg-muted))]">Years experience</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--fg-muted))]">Focus</dt>
                <dd className="mt-2 font-display text-3xl font-bold text-[hsl(var(--terracotta-deep))]">3</dd>
                <dd className="text-xs text-[hsl(var(--fg-muted))]">Core pillars</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--fg-muted))]">Region</dt>
                <dd className="mt-2 font-display text-3xl font-bold text-[hsl(var(--terracotta-deep))]">Africa</dd>
                <dd className="text-xs text-[hsl(var(--fg-muted))]">Cross-continent</dd>
              </div>
            </dl>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-[28px] overflow-hidden border border-[hsl(var(--line))] shadow-[0_30px_80px_-30px_rgba(28,25,23,0.35)]">
              <img
                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1200&q=80"
                alt="Solar panel array at sunrise"
                className="w-full h-[520px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--espresso))]/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <div className="bg-[hsl(var(--ivory))]/95 backdrop-blur-md rounded-2xl px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--fg-muted))]">Live project</div>
                  <div className="font-display text-base font-bold">Solar Farm · West Africa</div>
                </div>
                <div className="hidden sm:flex flex-col items-end text-right bg-[hsl(var(--terracotta))] text-white rounded-2xl px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Target ROI</div>
                  <div className="font-display text-lg font-bold">12 – 15%</div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-8 -left-8 hidden lg:block bg-white border border-[hsl(var(--line))] rounded-2xl p-4 shadow-xl w-60">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-[hsl(var(--leaf-soft))] text-[hsl(var(--leaf))] inline-flex items-center justify-center">
                  <i className="fa-solid fa-leaf"></i>
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--fg-muted))]">Impact</div>
                  <div className="font-semibold text-sm leading-snug">Clean energy + livelihoods</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
